// Featured Builds — public showcase pulling lab_build_shares where
// admin set status='Featured' AND permission_granted=true. No participant emails
// or admin notes returned by the backend, safe to render anonymously.
import { useEffect, useState } from "react";
import axios from "axios";
import { ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FeaturedBuilds() {
  const [builds, setBuilds] = useState([]);

  useEffect(() => {
    axios.get(`${API}/build-lab-followup/featured-builds`)
      .then((r) => setBuilds(r.data.builds || []))
      .catch(() => setBuilds([]));
  }, []);

  // Render nothing until at least one build is featured — keeps the page clean
  // for the very first launch before any builds are submitted.
  if (builds.length === 0) return null;

  return (
    <section id="featured-builds" className="bg-bone border-t border-b border-[var(--line)]" data-testid="featured-builds-section">
      <div className="max-w-5xl mx-auto px-4 py-16 sm:py-20">
        <div className="text-center mb-10">
          <div className="eyebrow mb-3">Featured Builds</div>
          <h2 className="font-display text-3xl sm:text-4xl tracking-[-0.025em] text-ink">
            Real builds from real participants.
          </h2>
          <p className="mt-4 text-ink/70 max-w-xl mx-auto">
            Websites, apps, dashboards, intake systems — everything below was shipped by someone who walked into the lab with an idea and walked out with a working build.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {builds.map((b) => (
            <article key={b.id} className="rounded-2xl border border-[var(--line)] bg-white overflow-hidden" data-testid={`featured-build-${b.id}`}>
              {b.screenshot_url && (
                <a href={b.build_link || b.screenshot_url} target="_blank" rel="noopener noreferrer" className="block aspect-[16/10] bg-bone overflow-hidden">
                  <img src={b.screenshot_url} alt={b.build_title} className="w-full h-full object-cover" loading="lazy" />
                </a>
              )}
              <div className="p-5">
                <p className="eyebrow !mb-1">{b.participant_name}</p>
                <h3 className="font-display text-xl tracking-[-0.015em] text-ink">{b.build_title}</h3>
                <p className="mt-2 text-sm text-ink/70 leading-relaxed line-clamp-3">{b.description}</p>
                {b.build_link && (
                  <a href={b.build_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mt-3 text-sm text-brand hover:underline">
                    See it live <ExternalLink size={11} />
                  </a>
                )}
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
